const separatorsCache = new Map<string, { decimal: string; group: string }>();

/**
 * Decimal and group separator of a locale, taken from Intl:
 * `en-US` → `.` / `,`, `sr-Latn` → `,` / `.`.
 */
export function getNumberSeparators(locale: string) {
  const cached = separatorsCache.get(locale);
  if (cached) return cached;

  const parts = new Intl.NumberFormat(locale).formatToParts(12345.6);
  const separators = {
    decimal: parts.find((part) => part.type === 'decimal')?.value ?? '.',
    group: parts.find((part) => part.type === 'group')?.value ?? ',',
  };
  separatorsCache.set(locale, separators);
  return separators;
}

/**
 * Strips everything that cannot be part of a number being typed and writes the
 * decimal separator the way the locale writes it. Both `.` and `,` are accepted
 * on input — a device keyboard offers whichever its own language uses — so the
 * value never depends on which keyboard the user happens to have.
 *
 * Kept intentionally lenient: a trailing separator (`175,`) and trailing zeros
 * (`1,50`) survive, because they are valid intermediate states while typing.
 */
export function normalizeNumberInput(input: string, locale: string) {
  const { decimal } = getNumberSeparators(locale);
  const isNegative = input.trimStart().startsWith('-');
  const cleaned = input.replace(/[^\d.,]/g, '');
  const digitsOf = (part: string) => part.replace(/[.,]/g, '');

  // Both characters present ("12.345,6") can only be grouping + decimal, so the
  // last one wins. A single kind is what typing produces — there the first one
  // is the decimal and any extra is dropped, so the field does not jump around.
  const decimalIndex =
    cleaned.includes('.') && cleaned.includes(',')
      ? Math.max(cleaned.lastIndexOf('.'), cleaned.lastIndexOf(','))
      : cleaned.search(/[.,]/);

  const body =
    decimalIndex < 0
      ? digitsOf(cleaned)
      : [
          digitsOf(cleaned.slice(0, decimalIndex)),
          digitsOf(cleaned.slice(decimalIndex + 1)),
        ].join(decimal);

  return isNegative ? `-${body}` : body;
}

/**
 * Typed text → number, or `null` when it does not (yet) spell out one.
 */
export function parseNumberInput(input: string, locale: string) {
  const { decimal } = getNumberSeparators(locale);
  const numeric = normalizeNumberInput(input, locale)
    .replace(decimal, '.')
    .replace(/\.$/, '');

  // Number('') is 0 and Number('-') is NaN — neither means "the user entered 0"
  if (numeric === '' || numeric === '-') return null;

  const parsed = Number(numeric);
  return Number.isFinite(parsed) ? parsed : null;
}

// `String(1e-7)` is "1e-7" — a later normalize pass would mangle that into
// "17". This formatter writes plain digits at any magnitude (20 is the widest
// fraction Intl accepts, enough for every representable double's short form).
const canonicalNumberFormat = new Intl.NumberFormat('en-US', {
  maximumFractionDigits: 20,
  useGrouping: false,
});

/**
 * Number → text for the input, using the locale's decimal separator and no
 * grouping (grouping is unwelcome inside an editable field).
 */
export function formatNumberInput(value: unknown, locale: string) {
  if (value === null || value === undefined || value === '') return '';

  const numeric = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(numeric)) return '';

  const { decimal } = getNumberSeparators(locale);
  return canonicalNumberFormat.format(numeric).replace('.', decimal);
}

/**
 * Decides whether a draft (the text as typed) must be replaced after a change
 * of the external value or the language; `null` means keep the user's text.
 *
 * A field that has never received a defined external value is uncontrolled —
 * its draft IS the value, and an `undefined` prop must not erase it. Once a
 * value has arrived (`hasBeenControlled`), `undefined` reads as an external
 * clear. Either way, a draft that merely spells the held number differently
 * (`1,` or `1,50` for 1.5) is the user mid-typing and stays untouched.
 */
export function resolveDraftResync({
  hasBeenControlled,
  previousLocale,
  draft,
  locale,
  value,
}: {
  hasBeenControlled: boolean;
  previousLocale: string;
  draft: string;
  locale: string;
  value: unknown;
}) {
  const hasLocaleChanged = previousLocale !== locale;

  if (value === undefined && !hasBeenControlled) {
    if (!hasLocaleChanged) return null;
    // normalize accepts either separator, so the old spelling parses fine
    const respelled = formatNumberInput(
      parseNumberInput(draft, locale),
      locale,
    );
    return respelled === draft ? null : respelled;
  }

  const incoming = formatNumberInput(value, locale);
  if (draft === incoming) return null;
  if (
    hasLocaleChanged ||
    parseNumberInput(draft, locale) !== parseNumberInput(incoming, locale)
  ) {
    return incoming;
  }
  return null;
}
